import { liveMethodologyVersion } from '@/features/bid-methodology/utils';
import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { useAtomValue } from 'jotai';
import {
  activeDeliverableEditorAtom,
  deliverableEditorAtom,
} from '@/features/bid-deliverables/store';
import { bidMethodologyEditorDocIdAtom } from '@/features/bid-methodology/store';
import { useExportEditorToDocx } from '@/ui/components/editor/export-to-docx';
import { useProjectView } from '@/features/projects/helpers';
import { Suspense, useState } from 'react';
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import z from 'zod';
import { bidMethodologyVersionsQuery } from '@/features/bid-methodology/queries';
import { useBidMethodologyChat } from '@/features/bid-methodology/helpers';
import {
  useExportBidMethodologyDocument,
  useReanalyzeBidMethodology,
} from '@/features/bid-methodology/mutations';
import { MethodologyEditor } from '@/features/bid-methodology/components/methodology-editor';
import { AnalysisStatus } from '@/features/workspaces/constants';
import { StaticFormattingToolbar } from '@/ui/components/editor/static-formatting-toolbar';
import { useSuspenseQuery } from '@tanstack/react-query';
import { BidBreadcrumbs } from '@/features/bids/components/bid-breadcrumbs';
import { BidDocumentEditor } from '@/features/bid-deliverables/components/bid-document-editor';
import { BidDeliverableStatusMenu } from '@/features/bid-deliverables/components/bid-deliverable-status-menu';
import { DeliverableScore } from '@/features/bid-deliverables/components/bid-deliverable-score';
import {
  DeliverableFileCombobox,
  DeliverableUploadsActionBar,
  pickDeliverableFile,
} from '@/features/bid-deliverables/components/bid-deliverable-uploads';
import { bidDeliverablesQuery } from '@/features/bid-deliverables/queries';
import { isEditableBidDocument } from '@/features/bid-deliverables/utils';
import { BID_QUALIFICATION_CRITERION_ID } from '@/features/bid-deliverables/constants';
import { bidComplianceQuery } from '@/features/bid-compliance/queries';
import { useDocument } from '@/features/documents/queries';
import { DocumentViewer } from '@/features/documents/preview/components/document-viewer';
import { downloadFileFromUrl } from '@/features/documents/utils';
import { Button } from '@/ui/components/button';
import { Icon } from '@/ui/components/icon';
import { Download01Icon, PlusSignIcon, Refresh01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { Group, GroupItem, GroupSeparator } from '@/ui/components/group';
import { BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton, HeaderChatButton } from '@/ui/components/header';
import { H1 } from '@/ui/components/heading';
import { MainHeader, MainHeaderBackButton, MainScrollArea } from '@/ui/components/main';
import { Spinner } from '@/ui/components/spinner';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/bids/$bidId/_layout/deliverables/$criterionId/$deliverableId',
)({
  validateSearch: z.object({
    commentThreadId: z.string().optional(),
  }),
  loader: async ({ context, params }) => {
    const [compliance] = await Promise.all([
      context.queryClient.query({
        ...bidComplianceQuery(params.workspaceId, params.bidId),
        staleTime: 'static',
      }),
      context.queryClient.query({
        ...bidMethodologyVersionsQuery(params.bidId),
        staleTime: 'static',
      }),
    ]);
    const item = compliance.deliverableCatalog?.items.find(
      (entry) => entry.id === params.deliverableId,
    );
    return {
      chatSurface:
        item?.executionPlan === true && item.files.length > 0
          ? ('bid_methodology' as const)
          : undefined,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId, workspaceId, bidId, criterionId, deliverableId } = Route.useParams();
  const view = useProjectView();
  const activeEditor = useAtomValue(deliverableEditorAtom);
  const methodologyEditorDocId = useAtomValue(bidMethodologyEditorDocIdAtom);
  const exportMethodology = useExportBidMethodologyDocument();
  const exportDocx = useExportEditorToDocx(activeDeliverableEditorAtom, {
    errorTitle: t(common.notifications.error),
  });
  const { data: compliance } = useSuspenseQuery(bidComplianceQuery(workspaceId, bidId));
  const { data: methodologyVersions } = useSuspenseQuery(bidMethodologyVersionsQuery(bidId));
  const liveMethodology = liveMethodologyVersion(methodologyVersions.versions);
  const methodologyReady = liveMethodology?.status === AnalysisStatus.DONE;
  const methodologyRunning = methodologyVersions.versions.some(
    (version) => version.status === AnalysisStatus.IN_PROGRESS,
  );
  const reanalyze = useReanalyzeBidMethodology();
  const navigate = useNavigate();
  const deliverable = useSuspenseQuery(bidDeliverablesQuery(workspaceId, bidId)).data.find(
    (item) => item.id === deliverableId,
  );
  const { data: document, error: documentError } = useDocument(deliverable?.file?.docId, projectId);
  const { chatSurface } = Route.useLoaderData();
  const editable = view === 'supplier' && !!document && isEditableBidDocument(document);
  const methodologyEditor = !!chatSurface && methodologyReady;
  const criterion = compliance.deliverableCatalog?.tenderCriteria.find(
    (item) => item.id === criterionId,
  );
  const qualification = criterionId === BID_QUALIFICATION_CRITERION_ID;
  const criterionName =
    criterion?.name ??
    (qualification ? t(common.qualification) : t({ en: 'Compliance', nb: 'Samsvar' }));

  if (!deliverable) throw notFound();
  if (documentError) throw documentError;

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/deliverables/$criterionId"
          params={{ projectId, workspaceId, bidId, criterionId }}
        />
        <BidBreadcrumbs>
          {qualification ? null : (
            <BreadcrumbLink
              to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/deliverables"
              params={{ projectId, workspaceId, bidId }}
              search={{ section: 'tender' }}
            >
              {t({ en: 'Tender', nb: 'Tilbud' })}
            </BreadcrumbLink>
          )}
          <BreadcrumbLink
            to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/deliverables/$criterionId"
            params={{ projectId, workspaceId, bidId, criterionId }}
          >
            {criterionName}
          </BreadcrumbLink>
          <BreadcrumbPage>{document?.name ?? '...'}</BreadcrumbPage>
        </BidBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <MainHeader>
        <H1>
          <MainHeaderBackButton
            to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/deliverables/$criterionId"
            params={{ projectId, workspaceId, bidId, criterionId }}
          />
          <span className="line-clamp-1">{deliverable.file?.filename ?? document?.name}</span>
        </H1>
        <div className="flex items-center gap-2">
          <Group>
            <GroupItem
              render={
                <Button
                  variant="secondary"
                  kind="with-icon"
                  onClick={() => pickDeliverableFile(deliverable)}
                />
              }
            >
              <Icon icon={PlusSignIcon} />
              {t({ en: 'Add new version', nb: 'Legg til ny versjon' })}
            </GroupItem>
            <GroupSeparator />
            <DeliverableFileCombobox deliverable={deliverable} size="md" removable={false} />
          </Group>
          {methodologyEditor ? (
            <Button
              variant="secondary"
              kind="with-icon"
              pending={reanalyze.isPending || methodologyRunning}
              onClick={() =>
                reanalyze.mutate(undefined, {
                  onSuccess: ({ skippedBecauseNoChanges }) => {
                    if (skippedBecauseNoChanges) return;
                    navigate({
                      to: '/project/$projectId/workspaces/$workspaceId/bids/$bidId/deliverables/$criterionId',
                      params: { projectId, workspaceId, bidId, criterionId },
                    });
                  },
                })
              }
            >
              <Icon icon={Refresh01Icon} />
              {t({ en: 'Rescore', nb: 'Vurder på nytt' })}
            </Button>
          ) : null}
          {document ? (
            <Button
              variant="secondary"
              kind="with-icon"
              pending={exportDocx.isPending || exportMethodology.isPending}
              disabled={
                methodologyEditor
                  ? methodologyEditorDocId !== document.id
                  : editable && activeEditor?.docId !== document.id
              }
              onClick={() => {
                const name = document.name.replace(/\.[^.]+$/, '');
                if (methodologyEditor) return exportMethodology.mutate(name);
                if (editable) return exportDocx.mutate(name);
                downloadFileFromUrl(document.sasUri, document.name);
              }}
            >
              <Icon icon={Download01Icon} />
              {t({ en: 'Export', nb: 'Eksporter' })}
            </Button>
          ) : null}
        </div>
      </MainHeader>
      <div className="relative border-t grow min-w-0 flex flex-col min-h-0">
        {!deliverable.file ? (
          <p className="p-4 text-sm text-muted">{t({ en: 'Missing', nb: 'Mangler' })}</p>
        ) : !document ? (
          <Spinner kind="inset" />
        ) : methodologyEditor ? (
          <DeliverableMethodologyEditor docId={document.id} />
        ) : editable ? (
          <BidDocumentEditor documentId={document.id} />
        ) : (
          <Suspense fallback={<Spinner kind="inset" />}>
            <DocumentViewer url={document.sasUri} extension={document.extension} />
          </Suspense>
        )}
      </div>
      <footer className="flex shrink-0 items-center justify-between gap-2 border-t px-x py-2">
        {deliverable.file &&
        (deliverable.executionPlan === true || deliverable.purpose === 'personnel_or_cv') ? (
          <DeliverableScore deliverable={deliverable} plain />
        ) : (
          <span className="text-sm text-muted">{t(common.status)}</span>
        )}
        <BidDeliverableStatusMenu deliverable={deliverable} />
      </footer>
      <DeliverableUploadsActionBar />
    </>
  );
}

function DeliverableMethodologyEditor({ docId }: { docId: string }) {
  const [toolbarPortal, setToolbarPortal] = useState<HTMLDivElement | null>(null);
  useBidMethodologyChat();

  return (
    <>
      <StaticFormattingToolbar setToolbarPortal={setToolbarPortal} />
      <MainScrollArea>
        <MethodologyEditor docId={docId} toolbarPortal={toolbarPortal} />
      </MainScrollArea>
    </>
  );
}
